export type SyncJobKind = 'progress' | 'file' | 'submit';

export interface SyncJob {
  kind: SyncJobKind;
  attemptId: string;
  userId: string;
  seq?: number;
  deviceId?: string;
  answers?: Record<string, unknown>;
  clientSubmissionId?: string;
  questionId?: string;
  idempotencyKey?: string;
}

export interface DrainResult {
  retryIn: number | null;
}

const ORDER: Record<SyncJobKind, number> = { progress: 0, file: 1, submit: 2 };

/** Exponential backoff with jitter, clamped to 1–30s. Full delay at random 0; up to half less as random approaches 1. */
export function backoffDelayMs(tryIndex: number, random: () => number): number {
  const full = Math.min(30, 2 ** Math.max(0, tryIndex));
  const span = full <= 1 ? 0 : full / 2;
  const roll = Math.min(1, Math.max(0, random()));
  const seconds = Math.min(30, Math.max(1, full - roll * span));
  return Math.round(seconds * 1000);
}

export function jobsForDraft(
  userId: string,
  attemptId: string,
  draft: {
    dirty?: boolean;
    pendingSubmit?: boolean;
    seq: number;
    deviceId: string;
    answers: Record<string, unknown>;
    clientSubmissionId: string | null;
  },
): SyncJob[] {
  const jobs: SyncJob[] = [];
  if (draft.dirty) {
    jobs.push({
      kind: 'progress',
      userId,
      attemptId,
      seq: draft.seq,
      deviceId: draft.deviceId,
      answers: draft.answers,
    });
  }
  if (draft.pendingSubmit && draft.clientSubmissionId) {
    jobs.push({
      kind: 'submit',
      userId,
      attemptId,
      answers: draft.answers,
      clientSubmissionId: draft.clientSubmissionId,
    });
  }
  return jobs;
}

export function isRetryableStatus(status: number): boolean {
  return status === 0 || status === 408 || status === 429 || status >= 500;
}

function jobKey(job: SyncJob): string {
  if (job.kind === 'file') return `file:${job.attemptId}:${job.idempotencyKey ?? ''}`;
  return `${job.kind}:${job.attemptId}`;
}

export function createAttemptSyncQueue(
  send: (job: SyncJob) => Promise<number>,
  options?: { sleep?: (ms: number) => Promise<void>; random?: () => number },
) {
  const jobs: SyncJob[] = [];
  const tries = new Map<string, number>();
  let draining: Promise<DrainResult> | null = null;
  const sleep = options?.sleep ?? ((ms: number) => new Promise((resolve) => setTimeout(resolve, ms)));
  const random = options?.random ?? Math.random;

  function enqueue(job: SyncJob) {
    if (job.kind === 'progress') {
      const index = jobs.findIndex((item) => item.kind === 'progress' && item.attemptId === job.attemptId);
      if (index >= 0) {
        jobs[index] = job;
        return;
      }
    }
    if (job.kind === 'submit') {
      const existing = jobs.find((item) => item.kind === 'submit' && item.attemptId === job.attemptId);
      if (existing) {
        existing.answers = job.answers;
        return;
      }
    }
    if (job.kind === 'file' && jobs.some((item) => item.kind === 'file' && item.idempotencyKey === job.idempotencyKey)) {
      return;
    }
    jobs.push(job);
  }

  function nextIndex(): number {
    let best = -1;
    let rank = Number.POSITIVE_INFINITY;
    for (let i = 0; i < jobs.length; i += 1) {
      const score = ORDER[jobs[i].kind] * 1000 + i;
      if (score < rank) {
        rank = score;
        best = i;
      }
    }
    return best;
  }

  function dropAttempt(attemptId: string) {
    for (let i = jobs.length - 1; i >= 0; i -= 1) {
      if (jobs[i].attemptId === attemptId) jobs.splice(i, 1);
    }
  }

  function drain(): Promise<DrainResult> {
    if (draining) return draining;
    draining = (async () => {
      while (jobs.length > 0) {
        const index = nextIndex();
        const job = jobs[index];
        const status = await send(job);
        if (status >= 200 && status < 300) {
          jobs.splice(index, 1);
          tries.delete(jobKey(job));
          continue;
        }
        if (status === 409) {
          dropAttempt(job.attemptId);
          continue;
        }
        if (status === 401) return { retryIn: null };
        if (status === 0) {
          const n = tries.get(jobKey(job)) ?? 0;
          tries.set(jobKey(job), n + 1);
          return { retryIn: backoffDelayMs(n, random) };
        }
        if (isRetryableStatus(status)) {
          const n = tries.get(jobKey(job)) ?? 0;
          tries.set(jobKey(job), n + 1);
          await sleep(backoffDelayMs(n, random));
          continue;
        }
        jobs.splice(index, 1);
      }
      return { retryIn: null };
    })().finally(() => {
      draining = null;
    });
    return draining;
  }

  return {
    enqueue,
    drain,
    snapshot: () => jobs.map((job) => ({ ...job })),
  };
}
